<script setup>
import {
  defineProps,
  computed,
  reactive,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import {
  differenceInCalendarDays,
  format,
  isThisYear,
  isToday,
  isYesterday,
} from 'date-fns';
import { localeDoApp } from 'shared/helpers/timeHelper';
import { useI18n } from 'vue-i18n';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry', 'forward', 'enterSelectionMode']);

const store = useStore();

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

// ── Separador de data (estilo WhatsApp/Digisac, cores n-*) ────────────────────
const messageDay = msg => {
  const ts = msg?.createdAt;
  if (!ts) return null;
  const day = new Date(ts > 1e12 ? ts : ts * 1000);
  // `ts` às vezes chega como string (não Unix timestamp numérico) — a
  // comparação `ts > 1e12` vira NaN>1e12 (false), cai no `ts*1000` (NaN),
  // e `new Date(NaN)` é um objeto Date SEMPRE truthy (mesmo inválido) —
  // os `if (!day)` mais abaixo nunca pegavam isso. Achado via sourcemap
  // como causa real da tela em branco pós-edição: `format()` no separador
  // de data jogava RangeError e derrubava a lista inteira.
  if (Number.isNaN(day.getTime())) return null;
  return day;
};
const showDateSeparator = index => {
  const list = allMessages.value;
  const day = messageDay(list[index]);
  if (!day) return false;
  if (index === 0) return true;
  const prev = messageDay(list[index - 1]);
  return !prev || day.toDateString() !== prev.toDateString();
};
const { t } = useI18n();

// O rótulo do dia segue os mensageiros: "Hoje", "Ontem", o DIA DA SEMANA
// dentro da última semana ("Segunda-feira" diz mais que "14/09/2026" para
// quem pensa em "o áudio que ela mandou na segunda") e, fora dela, a data por
// extenso — com o ano só quando não é o corrente.
// "Hoje" e "Ontem" eram texto cravado em português, fora da tradução.
const dateSeparatorLabel = msg => {
  const day = messageDay(msg);
  if (!day) return '';
  if (isToday(day)) return t('CONVERSATION.DAY_SEPARATOR.TODAY');
  if (isYesterday(day)) return t('CONVERSATION.DAY_SEPARATOR.YESTERDAY');
  // O idioma do APP, e não `ptBR` cravado: o fork atende outros idiomas, e
  // o nome do dia da semana tem que sair no da pessoa.
  const opcoes = { locale: localeDoApp() };
  let rotulo;
  if (differenceInCalendarDays(new Date(), day) < 7) {
    rotulo = format(day, 'EEEE', opcoes);
  } else if (isThisYear(day)) {
    rotulo = format(day, "d 'de' MMMM", opcoes);
  } else {
    rotulo = format(day, "d 'de' MMMM 'de' yyyy", opcoes);
  }
  return rotulo.charAt(0).toUpperCase() + rotulo.slice(1);
};

const currentChat = useMapGetter('getSelectedChat');

// Selection mode integration
const isSelectionMode = computed(
  () => store.getters['messageSelection/isSelectionMode']
);
const selectedMessageIds = computed(
  () => store.getters['messageSelection/getSelectedMessageIds']
);

const isMessageSelected = messageId => {
  return selectedMessageIds.value.includes(messageId);
};

const handleToggleSelection = messageId => {
  store.dispatch('messageSelection/toggleMessageSelection', messageId);
};

const handleForward = messageId => {
  emit('forward', messageId);
};

const handleEnterSelectionMode = messageId => {
  emit('enterSelectionMode', messageId);
};

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

const isFirstInGroup = (index, searchList) => {
  if (index === 0) return true;
  return !shouldGroupWithNext(index - 1, searchList);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};

// ── A DATA GRUDADA SOME QUANDO A ROLAGEM PARA (05/10/2026) ──────────────────
// Grudada no topo, a pílula do dia responde "de quando é isso?" enquanto se
// rola — mas parada ela cobria palavras da mensagem de baixo (medido no
// celular: "Maria, passa[ndo para lem]brar"). Como no WhatsApp: rolando, ela
// aparece; parou, a que está grudada some depois de 1,5s. A pílula no seu
// lugar, entre as mensagens, nunca some.
const lista = ref(null);
let rolagem = null;
let temporizador = null;
const pilulas = () => lista.value?.querySelectorAll('.msg-dia') || [];
const esconderGrudadas = () => {
  if (!rolagem) return;
  const topo = rolagem.getBoundingClientRect().top;
  pilulas().forEach(item => {
    const distancia = item.getBoundingClientRect().top - topo;
    // Grudada = parada no deslocamento do `top` do sticky (8px), não no
    // fluxo da lista.
    item.classList.toggle(
      'msg-dia--escondida',
      distancia >= 0 && distancia <= 12
    );
  });
};
// Só a data de baixo fica grudada (24/10/2026). Todas as pílulas grudam no MESMO ponto, e a
// de "Hoje", mais estreita, deixava aparecer as pontas da de "9 de setembro" por trás:
// "9 de Hojembro". Como no WhatsApp, a grudada que foi alcançada pela seguinte some.
const cobrirAnteriores = () => {
  if (!rolagem) return;
  const topo = rolagem.getBoundingClientRect().top;
  const grudadas = [...pilulas()].filter(item => {
    const distancia = item.getBoundingClientRect().top - topo;
    return distancia >= 0 && distancia <= 12;
  });
  pilulas().forEach(item =>
    item.classList.toggle(
      'msg-dia--coberta',
      grudadas.indexOf(item) > -1 && item !== grudadas.at(-1)
    )
  );
};
const aoRolar = () => {
  pilulas().forEach(item => item.classList.remove('msg-dia--escondida'));
  cobrirAnteriores();
  clearTimeout(temporizador);
  temporizador = setTimeout(esconderGrudadas, 1500);
};
onMounted(() => {
  // Quem rola costuma ser a própria lista (o MessagesView põe
  // `overflow-y-auto` na raiz deste componente); senão, o ancestral.
  let no = lista.value;
  while (no && !['auto', 'scroll'].includes(getComputedStyle(no).overflowY))
    no = no.parentElement;
  rolagem = no;
  rolagem?.addEventListener('scroll', aoRolar, { passive: true });
  nextTick(cobrirAnteriores);
  temporizador = setTimeout(esconderGrudadas, 1500);
});
onBeforeUnmount(() => {
  rolagem?.removeEventListener('scroll', aoRolar);
  clearTimeout(temporizador);
});
</script>

<template>
  <ul ref="lista" class="px-4">
    <!-- Sem fundo próprio: a lista é transparente para a marca d'água da
         conversa aparecer atrás (ver MessagesView). Pintar aqui e apagar lá
         com `!important` era uma contra-regra.
         Este comentário mora DENTRO do <ul> de propósito: antes da raiz, ele
         vira um segundo nó e o componente deixa de ter raiz única — o `$el`
         passa a ser o comentário, e o menu de contexto, que trava a rolagem
         por esse `$el`, quebrava ao abrir (16/09/2026). -->
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <!-- O separador GRUDA no topo enquanto se rola aquele dia: é o que
           responde "de quando é isso?" sem subir a conversa inteira. -->
      <li v-if="showDateSeparator(index)" class="msg-dia">
        <span class="msg-dia__pilula">{{ dateSeparatorLabel(message) }}</span>
      </li>
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, allMessages)"
        :is-first-in-group="isFirstInGroup(index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        :is-selection-mode="isSelectionMode"
        :is-selected="isMessageSelected(message.id)"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
        @toggle-selection="handleToggleSelection"
        @forward="handleForward"
        @enter-selection-mode="handleEnterSelectionMode"
      />
    </template>
    <slot name="after" />
  </ul>
</template>

<style scoped>
.msg-dia {
  transition: opacity var(--mov-2) var(--mov-curva);
  position: sticky;
  z-index: 5;
  top: var(--esp-2);
  display: flex;
  justify-content: center;
  margin: var(--esp-3) 0;
  list-style: none;
  pointer-events: none;
  user-select: none;
}

.msg-dia--escondida,
.msg-dia--coberta {
  opacity: 0;
}

/* A coberta sai de uma vez: esmaecer deixaria o "9 de Hojembro" à vista por 0,2s. */
.msg-dia--coberta {
  transition: none;
}

/* Pílula opaca e com a sombra de contato: grudada no topo, ela passa por
   cima das bolhas, e translúcida misturaria o texto das duas. */
.msg-dia__pilula {
  padding: var(--esp-1) var(--esp-3);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1));
  box-shadow: 0 1px 2px rgb(15 23 42 / 8%);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-11));
}
</style>
